import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import UserSettingsMenu from "./UserSettingsMenu";

const bannerStyle = {
  background: "var(--banner-background)",
  color: "var(--banner-text-color)",
  padding: "0.5rem 1rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  height: "56px",
  boxSizing: "border-box"
};

const logoStyle = {
  height: "40px",
  marginRight: "10px"
};

const titleStyle = {
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
  fontSize: "1.5rem",
  fontWeight: "bold",
  margin: 0,
  color: "var(--banner-text-color)"
};

const ChatIcon = () => (
  <svg
    width="15" height="15" viewBox="0 0 24 24"
    fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const WorkflowIcon = () => (
  <svg
    width="15" height="15" viewBox="0 0 24 24"
    fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg
    width="12" height="12" viewBox="0 0 24 24"
    fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
    style={{ transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="13" height="13" viewBox="0 0 24 24"
    fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const MODES = [
  { key: 'chat',      label: 'Chat',      icon: <ChatIcon />,     href: '/chat' },
  { key: 'workflow',  label: 'Workflows', icon: <WorkflowIcon />, href: '/workflow-builder' },
];

/**
 * Dropdown that lets the user switch between Chat and Workflow Builder.
 * Renders a compact trigger (icon + label + chevron) that opens a small
 * menu below. Closes on outside click or Escape.
 */
const ModeDropdown = ({ isWorkflow: isWorkflowProp, upward = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Auto-detect from URL when caller doesn't pass isWorkflow explicitly.
  const isWorkflow = isWorkflowProp ?? pathname.startsWith('/workflow-builder');
  const activeKey = isWorkflow ? 'workflow' : 'chat';
  const current = MODES.find((m) => m.key === activeKey);

  /* Close on outside click or Escape */
  useEffect(() => {
    if (!open) return;
    const onPointer = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="mode-dropdown" ref={ref}>
      <button
        className="mode-dropdown-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Switch mode"
      >
        <span className="mode-dropdown-icon" aria-hidden="true">{current.icon}</span>
        <span className="mode-dropdown-label">{current.label}</span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <ul
          className="mode-dropdown-menu"
          role="listbox"
          aria-label="Application mode"
          style={upward ? { top: 'auto', bottom: 'calc(100% + 6px)' } : {}}
        >
          {MODES.map((mode) => {
            const isActive = mode.key === activeKey;
            return (
              <li key={mode.key} role="option" aria-selected={isActive}>
                <button
                  className={`mode-dropdown-item${isActive ? ' mode-dropdown-item--active' : ''}`}
                  onClick={() => {
                    setOpen(false);
                    if (!isActive) navigate(mode.href);
                  }}
                  tabIndex={0}
                >
                  <span className="mode-dropdown-item-icon" aria-hidden="true">{mode.icon}</span>
                  {mode.label}
                  {isActive && (
                    <span className="mode-dropdown-check" aria-hidden="true">
                      <CheckIcon />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/**
 * Shared application header.
 *
 * Rendered in both chat mode and workflow-builder mode.
 * `isWorkflow` controls which mode is shown as active in the dropdown.
 */
const HeaderComponent = ({
  theme,
  toggleTheme,
  selectedApis,
  setSelectedApis,
  refreshHistories,
  resetMainPanel,
  refreshModelSettings,
  isWorkflow = false,
}) => {
  const navigate = useNavigate();
  return (
  <header style={bannerStyle}>
    <picture
      onClick={() => navigate('/chat')}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate('/chat'); }}
      style={{ cursor: 'pointer', display: 'contents' }}
    >
      <source srcSet="stackformAI.webp" type="image/webp" />
      <img
        src="stackformAI-sm.png"
        alt="Stackform Logo"
        role="button"
        style={{ ...logoStyle, cursor: 'pointer' }}
      />
    </picture>
    <h1
      style={{ ...titleStyle, cursor: 'pointer' }}
      onClick={() => navigate('/chat')}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate('/chat'); }}
    >stackform.<span className="gradient-beta">beta</span></h1>
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <ModeDropdown isWorkflow={isWorkflow} />
      <UserSettingsMenu
        theme={theme}
        toggleTheme={toggleTheme}
        selectedApis={selectedApis}
        setSelectedApis={setSelectedApis}
        refreshHistories={refreshHistories}
        resetMainPanel={resetMainPanel}
        refreshModelSettings={refreshModelSettings}
      />
    </div>
  </header>
  );
};

export const Header = React.memo(HeaderComponent);
export { ModeDropdown };
